import { DATE_FORMAT, Icon, Tooltip } from '@dev-spendesk/grapes';
import { Trans } from 'react-i18next';

import { EllipsisTooltip } from 'common/components/EllipsisTooltip';
import { useTranslation } from 'common/hooks/useTranslation';
import { formatMemberName } from 'modules/bookkeep/prepare-payables/models';
import { type Payable } from 'modules/payable/models';
import { UserAvatar } from 'modules/users/components';
import {
  getExpenditureAvatar,
  getExpenditureType,
  payableTypeToExpenditureType,
} from 'src/core/utils/entities/expenditure';
import { formatMonetaryValue } from 'src/core/utils/monetaryValue';

export const AmountCell = ({ payable }: { payable: Payable }) => {
  const amountInCompanyCurrency = formatMonetaryValue(payable.functionalAmount);

  const amountInExpenditureCurrency =
    payable.grossAmount.currency !== payable.functionalAmount.currency
      ? formatMonetaryValue(payable.grossAmount)
      : undefined;

  return (
    <span className="flex flex-col">
      {amountInCompanyCurrency}
      {amountInExpenditureCurrency && (
        <span className="text-content-secondary-bg-primary">
          {amountInExpenditureCurrency}
        </span>
      )}
    </span>
  );
};

export const DateCell = ({ payable }: { payable: Payable }) => {
  const { localeFormat } = useTranslation('global');

  return (
    <span className="block text-right">
      {localeFormat(new Date(payable.creationDate), DATE_FORMAT.CUSTOM, {
        dateStyle: 'medium',
        timeZone: 'UTC',
      })}
    </span>
  );
};

const BaseDescriptionCell = ({ payable }: { payable: Payable }) => {
  const { t } = useTranslation('global');

  let prepend;
  if (payable.type === 'mileage_allowance') {
    prepend = <span>{payable.mileageDetails?.distance}</span>;
  } else if (payable.type === 'per_diem_allowance') {
    prepend = t('payables.panel.perDiemDuration', {
      count: payable.perDiem?.duration ?? 0,
    });
  }

  if (payable.counterparty?.type === 'supplier') {
    let maybeInvoiceNumber;

    if (payable.documentaryEvidence?.type === 'invoice') {
      maybeInvoiceNumber = payable.documentaryEvidence.invoiceNumber;
    }

    if (payable.documentaryEvidence?.type === 'creditNote') {
      maybeInvoiceNumber = payable.documentaryEvidence.creditNoteNumber;
    }

    if (maybeInvoiceNumber) {
      prepend = (
        <Trans
          key="receiptNumber"
          i18nKey="payables.panel.receiptNumber"
          values={{ receiptNumber: maybeInvoiceNumber }}
          components={[<span className="text-content-primary" key="text" />]}
        />
      );
    }
  }

  const payableDescription = payable.description || t('misc.na');

  return (
    <span>
      {prepend && (
        <>
          {prepend}
          <span className="mx-4">•</span>
        </>
      )}
      {payableDescription}
    </span>
  );
};

export const DescriptionCell = ({ payable }: { payable: Payable }) => {
  const { t } = useTranslation('global');
  const { icon, color, type } = getExpenditureType(
    { type: payableTypeToExpenditureType(payable) },
    t,
  );

  return (
    <div className="flex items-center gap-8">
      <Tooltip content={type} triggerAsChild>
        <Icon name={icon} color={color} className="shrink-0" />
      </Tooltip>
      <EllipsisTooltip text={<BaseDescriptionCell payable={payable} />} />
    </div>
  );
};

export const SupplierCell = ({ payable }: { payable: Payable }) => {
  const { t } = useTranslation('global');

  const supplierId =
    payable.counterparty?.type === 'supplier'
      ? payable.counterparty?.id
      : payable.supplier?.id || '';

  const supplierName =
    payable.counterparty?.type === 'supplier'
      ? payable.counterparty.name
      : payable.supplier?.name || '';

  const { avatar, avatarTitle } = getExpenditureAvatar(
    {
      type: payableTypeToExpenditureType(payable),
      supplier: { id: supplierId, name: supplierName },
      size: 32,
    },
    t,
  );

  return (
    <span className="flex items-center gap-8">
      {avatar}
      <EllipsisTooltip text={avatarTitle} />
    </span>
  );
};

export const EmployeeCell = ({ payable }: { payable: Payable }) => {
  const { t } = useTranslation('global');
  const memberName = formatMemberName(payable.member, t);

  return (
    <span className="flex items-center gap-8">
      <UserAvatar
        id={payable.member?.id ?? ''}
        name={memberName}
        avatar={
          payable.member?.type !== 'invitee'
            ? payable.member?.avatar
            : undefined
        }
      />
      <div className="w-[calc(100%-40px)]">
        <EllipsisTooltip text={memberName} />

        {payable.costCenter && (
          <EllipsisTooltip
            text={payable.costCenter.name}
            className="text-content-secondary-bg-primary"
          />
        )}
      </div>
    </span>
  );
};
